import { v1 as uuid } from 'uuid'
import { getMonitoringConfig } from './config'
import { createProvider } from './providers/factory'
import type { MonitoringProvider } from './providers/types'
import type { ReportErrorOptions } from './types'

const pageViewId = uuid()

// Lazy-initialized provider instance
let providerInstance: MonitoringProvider | null = null

/**
 * Gets or creates the monitoring provider instance
 */
function getProvider(): MonitoringProvider {
  if (!providerInstance) {
    const config = getMonitoringConfig()
    providerInstance = createProvider(config)
  }
  return providerInstance
}

export function resetProvider(): void {
  providerInstance = null
}

export const addPageAction = (
  name: string,
  props?: Record<string, string | number>,
) => {
  const provider = getProvider()
  provider.addPageAction(name, {
    ...props,
    pageViewId,
  })
}

/**
 * Track an error to monitoring services (New Relic).
 *
 * Automatically filters 404 errors by default to reduce dashboard noise.
 * Use the `options` parameter to control filtering behavior.
 *
 * @param error - The error to track (Error object or string)
 * @param props - Additional properties to attach to the error
 * @param options - Options for controlling error reporting behavior
 *
 * @example
 * ```typescript
 * // Track a simple error
 * trackError(new Error('Something went wrong'))
 *
 * // Track with additional context
 * trackError(error, { userId: 123, action: 'checkout' })
 *
 * // Force report a 404 error
 * trackError(error, undefined, { skipAutoFilter: true })
 *
 * // Conditionally skip reporting
 * trackError(error, props, { skip: shouldSkip })
 * ```
 */
export const trackError = (
  error: Error | string,
  props?: Record<string, string | number>,
  options?: ReportErrorOptions,
) => {
  // Skip if explicitly requested
  if (options?.skip) {
    return
  }

  const provider = getProvider()
  provider.trackError(error, props, options)
}

/**
 * @deprecated Use `trackError` instead. This function is kept for backward compatibility.
 */
export const reportError = trackError

// This is not currently testable, see: https://github.com/facebook/jest/issues/5620
export const initializeReporting = () => {
  window.addEventListener('unhandledrejection', (event) => {
    trackError(event.reason)
  })
}

// Export configuration utilities
export {
  setMonitoringConfig,
  getMonitoringConfig,
  resetMonitoringConfig,
} from './config'

// Export types
export type {
  MonitoringConfig,
  MonitoringProvider,
  ProviderType,
  NewRelicConfig,
} from './providers/types'

export default {
  addPageAction,
  trackError,
  reportError, // Deprecated alias
}
